import React, { FunctionComponent } from 'react';

import {
  FooterTop as CommonFooterTop,
  HassleFreeClaimsContent,
  totalLossSettlementHeading,
} from 'scenes/common/components/Footer';
import {
  isBusinessError,
  isIneligiblePromoCodeError,
  isLoadingWithNoData,
  isPromoCodeError,
  isQuoteSaved,
} from 'scenes/common/helpers/apiData';
import { InsuranceName } from 'scenes/common/types';
import {
  shouldShowClaimCVP,
  showFlybuysInfoSection,
  showFooterMarketingCampaignInformation,
  showFooterQuoteInformation,
  showHassleFreeClaims,
  showSaveOnlineInformation,
} from 'scenes/home/components/helpers';
import { getDiscountReleatedCopy } from 'scenes/home/helpers/Discount/discount';
import { informationLinks } from 'scenes/home/helpers/informationLinks';
import {
  getCampaign,
  getCoverType,
  getPolicyStartDate,
  getQuoteNumber,
} from 'scenes/home/selectors';
import { HomeFullPageProps, HomeFunnelState } from 'scenes/home/types';

import { AwardCard } from 'scenes/common/components/Awards';
import { useAwards } from 'scenes/common/hooks/useAwards/useAwards';
import { useIsLargeScreen } from 'scenes/common/hooks/useMedia';
import { isBeforeEffectiveEndDate } from 'scenes/common/selectors/configSelectors';
import {
  listItems,
  supportFootnote,
  totalLossSettlementText,
} from './HassleFreeClaims';

const displayHassleFreeClaimsComp = (
  isLargeScreen: boolean,
  funnelState: HomeFunnelState
) =>
  isLargeScreen && shouldShowClaimCVP(funnelState)
    ? [
        {
          text: 'How we settle a total loss claim',
          component: totalLossSettlementText,
        },
      ]
    : [
        {
          text: 'Our claims support',
          component: (
            <HassleFreeClaimsContent
              supportBenefitsList={listItems}
              totalLossClaimsWording={totalLossSettlementText}
              totalLossClaimsHeading={totalLossSettlementHeading()}
              supportFootnote={supportFootnote}
            />
          ),
        },
      ];

export const Footer: FunctionComponent<Pick<
  HomeFullPageProps,
  'funnelState'
>> = ({ funnelState }) => {
  const isLargeScreen = useIsLargeScreen();
  const hasBusinessErrors =
    isBusinessError(funnelState.apiResponses.quoteDetails) &&
    !isPromoCodeError(funnelState.apiResponses.quoteDetails) &&
    !isIneligiblePromoCodeError(funnelState.apiResponses.quoteDetails);

  const isApiQuoteSaved = isQuoteSaved(funnelState.apiResponses.quoteDetails);

  const showQuoteInformation = !hasBusinessErrors && !isApiQuoteSaved;

  const isPageNotLoadingOrError =
    !hasBusinessErrors &&
    !isApiQuoteSaved &&
    !isLoadingWithNoData(funnelState.apiResponses.quoteDetails);
  const { homeAwardsToDisplay } = useAwards();

  return (
    <CommonFooterTop
      quoteNumber={getQuoteNumber(funnelState)}
      campaign={getCampaign(funnelState)}
      showQuoteInformation={showQuoteInformation}
      pageNotLoadingOrError={isPageNotLoadingOrError}
      showFlybuys={showFlybuysInfoSection(funnelState.step)}
      showFooterQuoteInformation={showFooterQuoteInformation(
        funnelState.flow,
        funnelState.step
      )}
      showMarketingCampaign={showFooterMarketingCampaignInformation(
        funnelState.flow
      )}
      additionalComponents={[
        ...(showHassleFreeClaims(funnelState.flow, funnelState.step)
          ? displayHassleFreeClaimsComp(isLargeScreen, funnelState)
          : []),
      ]}
      awardsToDisplay={homeAwardsToDisplay(funnelState).map((award) => () => (
        <AwardCard award={award} inFooter />
      ))}
      informationLinks={informationLinks}
      insuranceName={InsuranceName.Home}
      saveOnlineInformation={
        showSaveOnlineInformation(funnelState)
          ? getDiscountReleatedCopy(getCoverType(funnelState))
          : undefined
      }
      isBeforeEffectiveEndDate={isBeforeEffectiveEndDate(
        funnelState.apiResponses.config,
        getPolicyStartDate(funnelState)
      )}
    />
  );
};

export default Footer;
